import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import * as fromStore from '../../_store';
import {Store} from '@ngrx/store';
import { INDRAPax } from '@ibe.models/pax.indra.model.lib';
import * as _ from 'lodash';
import {take} from 'rxjs/operators';
import * as fromGlobalStore from '@ibe/store/index';

import { COUNTRY_CODE_ITALY, COUNTRY_CODE_PORTUGAL, SESSION_VAR_KEY_MARKET } from '@ibe.config';
import { insuranceAddressbyCountryValidator, insuranceCitybyCountryValidator, insuranceNIFbyCountryValidator, insurancePostalCodebyCountryValidator } from '@ibe.components/shared/sidebar-views/passengers-new/passengers-new.utils';
import { INDRAIbeAdminService } from '@ibe.services/ibeadmin.indra.service';

@Component({
  selector: 'app-passenger-detail-chubb',
  templateUrl: './passenger-detail-chubb.indra.component.html',
  styleUrls: ['./passenger-detail-chubb.component.scss']
})
export class INDRAPassengerDetailChubbComponent implements OnInit, OnDestroy {
	
	@Input() displayValidationMessages: boolean = false;
	@Input() insuranceFormShow: { vat_info: boolean, address_info: boolean } = null;
	
	public form: FormGroup = this.fb.group({
		vatNumber:  [null, [Validators.required]],
		address: [null, [Validators.required, Validators.pattern('[a-zA-Z0-9_ ]+')]],
		zipcode: [null, [Validators.required, Validators.pattern('[a-zA-Z0-9_-]+')]],
		city: [null, [Validators.required]],
	});
	
	public isITMarket: boolean = false;
	public isPTMarket: boolean = false;
	
	private sub$ = new Subscription();

	private _hasChanges: BehaviorSubject<boolean> = new BehaviorSubject(false);
	get hasChanges(): Observable<boolean> {
		return this._hasChanges.asObservable();
	}

	get hasErrorClassVatNumber(): string {
		return this.form.get('vatNumber').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	get hasErrorClassAddress(): string {
		return this.form.get('address').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	get hasErrorClassZipCode(): string {
		return this.form.get('zipcode').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}
	
	get hasErrorClassCity(): string {
		return this.form.get('city').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	get hasData(): boolean {
		return !_.isEmpty(this.form.value);
	}

	get hasErrors(): boolean {
		return !_.isEmpty(this.form.errors);
	}

	constructor(
		private fb: FormBuilder,
		private store: Store<fromStore.State>,
		private globalStore: Store<fromGlobalStore.State>,
		private ibeAdminService: INDRAIbeAdminService,
  	) { 
		const market = sessionStorage.getItem(SESSION_VAR_KEY_MARKET);
		if (market == COUNTRY_CODE_ITALY) {
			this.isITMarket = true;
			this.form = this.fb.group({
				vatNumber:  [null, [Validators.required, insuranceNIFbyCountryValidator(market)]],
			});
		} else if (market === COUNTRY_CODE_PORTUGAL) {
			this.isPTMarket = true;
			this.form = this.fb.group({
				vatNumber:  [null, [Validators.required, insuranceNIFbyCountryValidator(market)]],
				address: [null, [Validators.required, insuranceAddressbyCountryValidator(market)]],
				zipcode: [null, [Validators.required, insurancePostalCodebyCountryValidator(market)]],
				city: [null, [Validators.required, insuranceCitybyCountryValidator(market)]],
			});
		}
	}

	ngOnInit() {
		this.sub$.add(this.store.select(fromStore.getSelectedPassengersAtIndex)
			.subscribe( passenger => {
					if (passenger) {
						this.mapPassengerToForm(passenger);
					}
				}
			)
		);
		this.sub$.add(
			this.form.valueChanges.subscribe(() => this._hasChanges.next(true))
		);

		this.formValueChangesUpdateTravelInsurance();
	}
	
	private formValueChangesUpdateTravelInsurance() {
		this.sub$.add(
			this.form.get('vatNumber').valueChanges.subscribe(() => this.dispatchUpdateTravelInsurance())
		);
		
		if (!this.isITMarket) {
			this.sub$.add(
				this.form.get('address').valueChanges.subscribe(() => this.dispatchUpdateTravelInsurance())
			);
			this.sub$.add(
				this.form.get('zipcode').valueChanges.subscribe(() => this.dispatchUpdateTravelInsurance())
			);
			this.sub$.add(
				this.form.get('city').valueChanges.subscribe(() => this.dispatchUpdateTravelInsurance())
			);
		}
		
		this.dispatchUpdateTravelInsurance();
	}

	private dispatchUpdateTravelInsurance() {
		this.store.dispatch(
			new fromStore.UpdateFormChangedAction({ 'updateTravelInsurance': true })
		);
	}

	private mapPassengerToForm(data: INDRAPax) {
		this.form.patchValue({ 	
			vatNumber : data.travelInsurance ? data.travelInsurance.vatNumber : '',
			address: data.travelInsurance ? data.travelInsurance.address : '',
			zipcode: data.travelInsurance ? data.travelInsurance.zipcode : '',
			city : data.travelInsurance ? data.travelInsurance.city : ''
		}, {emitEvent: false});
	}

	ngOnDestroy() {
		this.sub$.unsubscribe();
	}

	public trackByFn(index: number, item: any): any {
		return index;
	}

	public formTravelInsuranceErrors(control: string) {
		const errors = [];
		switch (control) {
			case 'address':
				if (this.form.controls['address'].hasError('emptyValue') || this.form.controls['address'].hasError('required')) {
					errors.push('bookingDetails.error.addressRequired');
				} else {
					if (this.isPTMarket) {
						errors.push('bookingDetails.error.addressInvalidFiveLetters');
					} else {
						errors.push('bookingDetails.error.addressInvalid');
					}
				}
				break;
			case 'zipcode':
				if (this.form.controls['zipcode'].hasError('emptyValue') || this.form.controls['zipcode'].hasError('required')) {
					errors.push('bookingDetails.error.zipcodeRequired');
				} else {
					errors.push('bookingDetails.error.zipcodeInvalid');
				}
				break;
			case 'city':
				if (this.form.controls['city'].hasError('emptyValue')  || this.form.controls['city'].hasError('required')) {
					errors.push('bookingDetails.error.cityRequired');
				} else {
					if (this.isPTMarket) {
						errors.push('bookingDetails.error.cityInvalidTwoLettersNoDigits');
					}
				}
				break;
			default:
				if (this.form.controls['vatNumber'].hasError('emptyValue') || this.form.controls['vatNumber'].hasError('required')) {
					errors.push('bookingDetails.error.vatNumberRequired');
				} else {
					errors.push(this.ibeAdminService.getTranslationValue('bookingDetails.error.vatNumberInvalid'));
					if (this.isPTMarket) {
						errors.push(this.ibeAdminService.getTranslationValue('bookingDetails.error.useNineDigits'));
					} else if (this.isITMarket) {
						errors.push(this.ibeAdminService.getTranslationValue('bookingDetails.error.useVatNumberItalianFormat'));
					}
				}
				break;
		}
		return errors.join(' ');
	}

	public clearForm() {
		this.form.reset({emitEvent: false});
	}

	public cancelEdit() {
		this.store.select(fromStore.getSelectedPassengersAtIndex)
			.pipe(take(1)).subscribe((data: INDRAPax) => {
				this.form.reset({
					vatNumber : data.travelInsurance ? data.travelInsurance.vatNumber : '',
					address: data.travelInsurance ? data.travelInsurance.address : '',
					zipcode: data.travelInsurance ? data.travelInsurance.zipcode : '',
					city : data.travelInsurance ? data.travelInsurance.city : ''
				}, {emitEvent: false});

				this._hasChanges.next(false);
			});
		this.store.dispatch(
			new fromStore.UpdateFormChangedAction(
				{
					'updateTravelInsurance': false,
				}
			)
		);
	}
}
